// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
// eslint-disable-next-line lodash/import-scope
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { Title, Text, Box, CenteredContainer } from '@nerdwallet/currency';
import BackgroundColor from '~/client/components/background-color/background-color';

import {
  repeatable,
  textColorPicker,
  titleInput,
  boolCheckboxSpec,
  subtitleInput,
  anchorIdInput,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import classColor from '../../lib/parse-color';

import getValidColor from '../../lib/get-valid-color';

import ZigZagSectionStretch from './zig-zag-section-stretch';

import './zig-zag.less';

const titleTextColorPicker = {
  ...textColorPicker,
  fieldName: 'titleTextColor',
  fieldLabel: 'Title text color',
};

const ZigZag = ({
  anchorId,
  bgOptions,
  titleStyle,
  title,
  backgroundColor,
  titleTextColor,
  subtitle,
  sections,
  size,
  imageContentFirst,
}) => {
  let wrapperStyle = {};
  if (backgroundColor && !bgOptions) {
    wrapperStyle = {
      backgroundColor: getValidColor(backgroundColor),
    };
  }

  const Sections = sections.map((section) => (
    <ZigZagSectionStretch
      {...section}
      textColor={titleTextColor}
      key={section.header}
      size={size}
      imageContentFirst={imageContentFirst}
    />
  ));

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(bgColor) => {
        wrapperStyle = !isEmpty(wrapperStyle)
          ? wrapperStyle
          : { backgroundColor: bgColor };
        return (
          <div style={wrapperStyle}>
            <CenteredContainer id={anchorId} className="zig-zag">
              <Title
                className={classNames([
                  'zig-zag__title',
                  classColor(titleTextColor),
                  titleStyle,
                ])}
                component="h2"
                size="small"
              >
                {title}
              </Title>
              {subtitle && (
                <Box
                  className={classNames([
                    'zig-zag__subtitle',
                    classColor(titleTextColor),
                  ])}
                >
                  <Text>{subtitle}</Text>
                </Box>
              )}
              <ul className="zig-zag__sections">{Sections}</ul>
            </CenteredContainer>
          </div>
        );
      }}
    </BackgroundColor>
  );
};

ZigZag.propTypes = {
  anchorId: PropTypes.string,
  title: PropTypes.string,
  titleTextColor: PropTypes.string,
  titleStyle: PropTypes.oneOf([
    'gotham-regular',
    'gotham-black',
    'chronicle-regular',
  ]),
  subtitle: PropTypes.string,
  subtitleWidthPercentage: PropTypes.string,
  ...BackgroundColor.propTypes,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      backgroundAlt: PropTypes.string,
      backgroundHeight: PropTypes.number,
      backgroundHeightMobile: PropTypes.number,
      backgroundUrl: PropTypes.string,
      backgroundUrlMobile: PropTypes.string,
      backgroundWidth: PropTypes.number,
      backgroundWidthMobile: PropTypes.number,
      header: PropTypes.string,
      content: PropTypes.string,
      contentMobile: PropTypes.string,
      textColor: PropTypes.string,
    })
  ),
  backgroundColor: PropTypes.string,
  size: PropTypes.oneOf(['', 'sm', 'md', 'lg']),
  imageContentFirst: PropTypes.bool,
};

ZigZag.getEditingConfig = (componentDocument) => [
  ...BackgroundColor.getEditingConfig(componentDocument),
  titleTextColorPicker,
  stringSelectSpec('titleStyle', 'Title font family', [
    { label: 'Gotham', value: 'gotham-regular' },
    { label: 'Gotham Black', value: 'gotham-black' },
    { label: 'Chronicle', value: 'chronicle-regular' },
  ]),
  titleInput,
  subtitleInput,
  stringInputSpec(
    'subtitleWidthPercentage',
    'Max-width of subtitle (in %, not pixels)'
  ),
  anchorIdInput,
  boolCheckboxSpec(
    'imageContentFirst',
    'First zig-zag element an image? (instead of text content)'
  ),
  repeatable('sections', 'Elements', 'Element', () =>
    ZigZagSectionStretch.getEditingConfig()
  ),
];

ZigZag.defaultProps = {
  size: '',
  sections: [],
  imageContentFirst: false,
  titleStyle: 'gotham',
  titleTextColor: '',
};

ZigZag.displayName = 'ZigZag';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(ZigZag);
